import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import toast from "../../../utils/notify";
import { nextId } from "../data/mockData";
import * as socApi from "../../../services/axios/AuthApis/SOCApis";

// =====================================================================
// SOCContext
// =====================================================================
// Single source of truth for all SOC domain state (alerts, assets,
// incidents, events, investigations, responses, threats, reports,
// notifications), now backed by the real /api/soc/ REST API
// (django_backend/virtual_soc) instead of fabricated frontend data.
// Mounted ONCE at the root of the SOC page (see SOC/index.js) so that
// navigating between sidebar tabs never remounts it and never
// triggers duplicate fetches for data already loaded this session.
//
// Field-name mapping: the backend serializers use snake_case Mongo
// field names (alert_id, detection_time, asset_name, ...); every
// existing SOC tab component was built against camelCase objects
// keyed by a human-readable `id` (e.g. "ALT-00001"). The normalize*
// helpers below translate backend responses into that exact same
// shape so none of the tab/table/drawer components need to change.
// Each domain's frontend `id` is deliberately set to the backend's
// human-readable id field (alert_id/incident_id/investigation_id/...)
// rather than the Mongo ObjectId, since that's what's rendered in
// tables ("Alert ID" column, etc) and what the backend's own detail/
// status URLs expect as the path parameter.
// =====================================================================

const SOCContext = createContext(null);

export const useSOC = () => {
  const ctx = useContext(SOCContext);
  if (!ctx) throw new Error("useSOC must be used within a SOCProvider");
  return ctx;
};

const SCAN_INTERVAL_MS = 60 * 1000; // 1 minute
const SCAN_DURATION_MS = 1200; // how long "Scan Running" is shown before refreshing

// ---------------------------------------------------------------
// Backend -> frontend field-name normalizers
// ---------------------------------------------------------------
const normAlert = (a) => ({
  id: a.alert_id,
  mongoId: a.id,
  name: a.name,
  description: a.description,
  severity: a.severity,
  source: a.source,
  sourceIp: a.source_ip,
  destination: a.destination,
  user: a.user,
  assetId: a.asset_id,
  assetName: a.asset_name,
  detectionTime: a.detection_time,
  createdAt: a.created_at,
  status: a.status,
  analyst: a.analyst,
  mitre: a.mitre_technique,
  detectionReason: a.detection_reason,
  relatedEventId: a.related_event_id,
  relatedIncidentId: a.related_incident_id,
  relatedInvestigationId: a.related_investigation_id,
  isRead: a.is_read,
  notes: a.notes || [],
  timeline: a.timeline || [],
});

const normIncident = (i) => ({
  id: i.incident_id,
  mongoId: i.id,
  name: i.name,
  severity: i.severity,
  category: i.category,
  source: i.source,
  affectedAssetId: i.affected_asset_id,
  affectedAssetName: i.affected_asset_name,
  affectedUser: i.affected_user,
  createdTime: i.created_time,
  analyst: i.analyst,
  status: i.status,
  priority: i.priority,
  relatedAlertIds: i.related_alert_ids || [],
  relatedEventIds: i.related_event_ids || [],
  affectedAssetIds: i.affected_asset_ids || [],
  affectedUserIds: i.affected_user_ids || [],
  investigationId: i.investigation_id,
  responseActionIds: i.response_action_ids || [],
  summary: i.summary || "",
  notes: i.notes || [],
  resolution: i.resolution,
  timeline: i.timeline || [],
});

const normInvestigation = (inv) => ({
  id: inv.investigation_id,
  mongoId: inv.id,
  title: inv.title,
  relatedAlertId: inv.related_alert_id,
  relatedIncidentId: inv.related_incident_id,
  analyst: inv.analyst,
  priority: inv.priority,
  status: inv.status,
  startedAt: inv.started_at,
  lastUpdated: inv.last_updated,
  summary: inv.summary || "",
  evidence: inv.evidence || [],
  events: inv.events || [],
  assets: inv.assets || [],
  users: inv.users || [],
  ips: inv.ips || [],
  iocs: inv.iocs || [],
  notes: inv.notes || [],
  findings: inv.findings || "",
  recommendedActions: inv.recommended_actions || [],
  timeline: inv.timeline || [],
});

const normEvent = (e) => ({
  id: e.event_id,
  mongoId: e.id,
  timestamp: e.timestamp,
  eventId: e.event_id,
  type: e.type,
  source: e.source,
  sourceIp: e.source_ip,
  user: e.user,
  assetId: e.asset_id,
  assetName: e.asset_name,
  action: e.action,
  result: e.result,
  severity: e.severity,
});

const normAsset = (a) => ({
  id: a.asset_id,
  mongoId: a.id,
  name: a.name,
  type: a.type,
  ip: a.ip,
  os: a.os,
  environment: a.environment,
  owner: a.owner,
  criticality: a.criticality,
  riskScore: a.risk_score,
  lastSeen: a.last_seen,
  status: a.status,
});

const normResponse = (r) => ({
  id: r.action_id,
  mongoId: r.id,
  action: r.action,
  target: r.target,
  triggeredBy: r.triggered_by,
  time: r.time,
  status: r.status,
  relatedIncidentId: r.related_incident_id,
});

const normReport = (r) => ({
  id: r.report_id,
  mongoId: r.id,
  type: r.report_type,
  dateFrom: r.date_from,
  dateTo: r.date_to,
  severityFilter: r.severity_filter,
  statusFilter: r.status_filter,
  generatedAt: r.generated_at,
  generatedBy: r.generated_by,
  summary: r.summary || {},
});

const normThreat = (t) => ({
  id: t.ioc_id,
  mongoId: t.id,
  ioc: t.ioc,
  type: t.type,
  threat: t.threat,
  actor: t.actor,
  confidence: t.confidence,
  firstSeen: t.first_seen,
  lastSeen: t.last_seen,
  source: t.source,
  status: t.status,
});

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });

// Large-but-bounded page size so existing tables (which do their own
// client-side search/sort/pagination over the `rows` they're given)
// keep working unchanged, while still asking the backend for a
// specific page instead of an unbounded "everything" query.
const LIST_PAGE_SIZE = 100;

export const SOCProvider = ({ children }) => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [responses, setResponses] = useState([]);
  const [threats, setThreats] = useState([]);
  const [reports, setReports] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [loading, setLoading] = useState({
    dashboard: false, alerts: false, incidents: false, investigations: false,
    events: false, assets: false, responses: false, threats: false, reports: false,
  });
  const [errors, setErrors] = useState({
    dashboard: null, alerts: null, incidents: null, investigations: null,
    events: null, assets: null, responses: null, threats: null, reports: null,
  });

  const setLoadingFlag = useCallback((key, val) => setLoading((p) => ({ ...p, [key]: val })), []);
  const setErrorFlag = useCallback((key, val) => setErrors((p) => ({ ...p, [key]: val })), []);

  const [scan, setScan] = useState({
    status: "Idle", // Idle | Running | Completed | Failed
    lastScanTime: null,
    nextScanTime: new Date(Date.now() + SCAN_INTERVAL_MS).toISOString(),
    issuesFound: 0,
    latestDetection: null,
  });

  // Navigation hook set by the SOC shell (index.js) so a notification
  // click can switch tabs / open a detail drawer.
  const navigateRef = useRef(null);
  const setNavigator = useCallback((fn) => {
    navigateRef.current = fn;
  }, []);
  const navigateTo = useCallback((tab, id) => {
    if (navigateRef.current) navigateRef.current(tab, id);
  }, []);

  const scanIntervalRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const startedRef = useRef(false);
  // Snapshot of alert ids already seen, so the scan refresh can tell
  // which alerts (created by the real backend scanner) are new since
  // the last check and notify about exactly those - not fabricate any.
  const knownAlertIdsRef = useRef(new Set());

  const pushNotification = useCallback((notification) => {
    setNotifications((prev) => [
      { id: nextId("NTF"), read: false, time: new Date().toISOString(), ...notification },
      ...prev,
    ]);
  }, []);

  // ---- Fetchers (real API, one per domain) ----

  const fetchDashboard = useCallback(async () => {
    setLoadingFlag("dashboard", true);
    setErrorFlag("dashboard", null);
    try {
      const data = await socApi.fetchSOCDashboard();
      setDashboardStats(data);
    } catch (err) {
      console.error("SOC dashboard fetch error:", err);
      setErrorFlag("dashboard", "Unable to load dashboard data.");
    } finally {
      setLoadingFlag("dashboard", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const fetchAlerts = useCallback(async (params = {}) => {
    setLoadingFlag("alerts", true);
    setErrorFlag("alerts", null);
    try {
      const data = await socApi.fetchSOCAlerts({ page_size: LIST_PAGE_SIZE, ...params });
      const normalized = (data.results || []).map(normAlert);
      setAlerts(normalized);
      normalized.forEach((a) => knownAlertIdsRef.current.add(a.id));
      return normalized;
    } catch (err) {
      console.error("SOC alerts fetch error:", err);
      setErrorFlag("alerts", "Unable to load alerts.");
      return [];
    } finally {
      setLoadingFlag("alerts", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const fetchIncidents = useCallback(async (params = {}) => {
    setLoadingFlag("incidents", true);
    setErrorFlag("incidents", null);
    try {
      const data = await socApi.fetchSOCIncidents({ page_size: LIST_PAGE_SIZE, ...params });
      setIncidents((data.results || []).map(normIncident));
    } catch (err) {
      console.error("SOC incidents fetch error:", err);
      setErrorFlag("incidents", "Unable to load incidents.");
    } finally {
      setLoadingFlag("incidents", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const fetchInvestigations = useCallback(async (params = {}) => {
    setLoadingFlag("investigations", true);
    setErrorFlag("investigations", null);
    try {
      const data = await socApi.fetchSOCInvestigations({ page_size: LIST_PAGE_SIZE, ...params });
      setInvestigations((data.results || []).map(normInvestigation));
    } catch (err) {
      console.error("SOC investigations fetch error:", err);
      setErrorFlag("investigations", "Unable to load investigations.");
    } finally {
      setLoadingFlag("investigations", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const fetchEvents = useCallback(async (params = {}) => {
    setLoadingFlag("events", true);
    setErrorFlag("events", null);
    try {
      const data = await socApi.fetchSOCEvents({ page_size: LIST_PAGE_SIZE, ...params });
      setEvents((data.results || []).map(normEvent));
    } catch (err) {
      console.error("SOC events fetch error:", err);
      setErrorFlag("events", "Unable to load events.");
    } finally {
      setLoadingFlag("events", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const fetchAssets = useCallback(async (params = {}) => {
    setLoadingFlag("assets", true);
    setErrorFlag("assets", null);
    try {
      const data = await socApi.fetchSOCAssets({ page_size: LIST_PAGE_SIZE, ...params });
      setAssets((data.results || []).map(normAsset));
    } catch (err) {
      console.error("SOC assets fetch error:", err);
      setErrorFlag("assets", "Unable to load assets.");
    } finally {
      setLoadingFlag("assets", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const fetchResponses = useCallback(async (params = {}) => {
    setLoadingFlag("responses", true);
    setErrorFlag("responses", null);
    try {
      const data = await socApi.fetchSOCResponseActions({ page_size: LIST_PAGE_SIZE, ...params });
      setResponses((data.results || []).map(normResponse));
    } catch (err) {
      console.error("SOC response actions fetch error:", err);
      setErrorFlag("responses", "Unable to load response actions.");
    } finally {
      setLoadingFlag("responses", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const fetchThreats = useCallback(async (params = {}) => {
    setLoadingFlag("threats", true);
    setErrorFlag("threats", null);
    try {
      const data = await socApi.fetchSOCThreatIntel({ page_size: LIST_PAGE_SIZE, ...params });
      setThreats((data.results || []).map(normThreat));
    } catch (err) {
      console.error("SOC threat intel fetch error:", err);
      setErrorFlag("threats", "Unable to load threat intelligence.");
    } finally {
      setLoadingFlag("threats", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const reportsLoadedRef = useRef(false);
  const fetchReports = useCallback(async (params = {}, force = false) => {
    if (reportsLoadedRef.current && !force) return; // avoid duplicate calls on tab revisit
    reportsLoadedRef.current = true;
    setLoadingFlag("reports", true);
    setErrorFlag("reports", null);
    try {
      const data = await socApi.fetchSOCReports({ page_size: LIST_PAGE_SIZE, ...params });
      setReports((data.results || []).map(normReport));
    } catch (err) {
      console.error("SOC reports fetch error:", err);
      setErrorFlag("reports", "Unable to load reports.");
    } finally {
      setLoadingFlag("reports", false);
    }
  }, [setLoadingFlag, setErrorFlag]);

  const generateReport = useCallback(async (reportType, params = {}) => {
    try {
      const created = await socApi.generateSOCReport({ report_type: reportType, ...params });
      const report = normReport(created);
      setReports((prev) => [report, ...prev]);
      toast.success(`${reportType} generated.`);
      return report;
    } catch (err) {
      console.error("SOC report generation error:", err);
      toast.error("Unable to generate report.");
      throw err;
    }
  }, []);

  // ---- Mutations (real API, update local state from the response) ----

  const escalateToIncident = useCallback(async (alert) => {
    try {
      const payload = {
        name: `Incident: ${alert.name}`,
        severity: alert.severity,
        category: "",
        source: alert.source,
        affected_asset_id: alert.assetId,
        affected_asset_name: alert.assetName,
        affected_user: alert.user,
        priority: alert.severity === "critical" ? "P1" : alert.severity === "high" ? "P2" : "P3",
        related_alert_ids: [alert.id],
        summary: `Incident opened from alert "${alert.name}" detected on ${alert.assetName}.`,
      };
      const created = await socApi.createSOCIncident(payload);
      const incident = normIncident(created);
      setIncidents((prev) => [incident, ...prev]);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alert.id ? { ...a, relatedIncidentId: incident.id } : a))
      );
      toast.success(`Incident ${incident.id} created.`);
      return incident;
    } catch (err) {
      console.error("Escalate to incident failed:", err);
      toast.error("Unable to create incident.");
      return null;
    }
  }, []);

  const openInvestigation = useCallback(async (incident, alert) => {
    if (!incident) return null;
    try {
      const payload = {
        title: `Investigation: ${incident.name}`,
        related_alert_id: alert ? alert.id : undefined,
        related_incident_id: incident.id,
        priority: incident.priority,
        summary: `Investigating root cause for ${incident.name}.`,
        assets: incident.affectedAssetIds || [],
        users: incident.affectedUserIds || [],
        ips: alert ? [alert.sourceIp, alert.destination].filter(Boolean) : [],
      };
      const created = await socApi.createSOCInvestigation(payload);
      const investigation = normInvestigation(created);
      setInvestigations((prev) => [investigation, ...prev]);
      // The backend already links the incident's investigation_id
      // server-side (see create_investigation) - mirror that locally
      // too so the UI reflects it immediately without a refetch.
      setIncidents((prev) =>
        prev.map((i) => (i.id === incident.id ? { ...i, investigationId: investigation.id } : i))
      );
      toast.success(`Investigation ${investigation.id} opened.`);
      return investigation;
    } catch (err) {
      console.error("Open investigation failed:", err);
      toast.error("Unable to open investigation.");
      return null;
    }
  }, []);

  const triggerResponseAction = useCallback(async (actionType, target, incident) => {
    try {
      const created = await socApi.createSOCResponseAction({
        action: actionType,
        target: target || "",
        related_incident_id: incident ? incident.id : undefined,
      });
      const action = normResponse(created);
      setResponses((prev) => [action, ...prev]);
      toast.success(`${actionType} triggered.`);
      return action;
    } catch (err) {
      console.error("Trigger response action failed:", err);
      toast.error("Unable to trigger response action.");
      return null;
    }
  }, []);

  const updateResponseActionStatus = useCallback(async (actionId, status) => {
    try {
      const updated = await socApi.updateSOCResponseActionStatus(actionId, status);
      const normalized = normResponse(updated);
      setResponses((prev) => prev.map((r) => (r.id === actionId ? normalized : r)));
      return normalized;
    } catch (err) {
      console.error("Update response action status failed:", err);
      toast.error("Unable to update response action status.");
      return null;
    }
  }, []);

  const updateAlertStatus = useCallback(async (alertId, status, analyst) => {
    try {
      const updated = await socApi.updateSOCAlertStatus(alertId, status, analyst);
      const normalized = normAlert(updated);
      setAlerts((prev) => prev.map((a) => (a.id === alertId ? normalized : a)));
    } catch (err) {
      console.error("Update alert status failed:", err);
      toast.error("Unable to update alert status.");
    }
  }, []);

  const markAlertRead = useCallback(async (alertId) => {
    try {
      const updated = await socApi.markSOCAlertRead(alertId);
      const normalized = normAlert(updated);
      setAlerts((prev) => prev.map((a) => (a.id === alertId ? normalized : a)));
    } catch (err) {
      console.error("Mark alert read failed:", err);
    }
  }, []);

  const updateIncidentStatus = useCallback(async (incidentId, status) => {
    try {
      const updated = await socApi.updateSOCIncidentStatus(incidentId, status);
      const normalized = normIncident(updated);
      setIncidents((prev) => prev.map((i) => (i.id === incidentId ? normalized : i)));
    } catch (err) {
      console.error("Update incident status failed:", err);
      toast.error("Unable to update incident status.");
    }
  }, []);

  const updateInvestigationStatus = useCallback(async (investigationId, status) => {
    try {
      const updated = await socApi.updateSOCInvestigationStatus(investigationId, status);
      const normalized = normInvestigation(updated);
      setInvestigations((prev) => prev.map((i) => (i.id === investigationId ? normalized : i)));
    } catch (err) {
      console.error("Update investigation status failed:", err);
      toast.error("Unable to update investigation status.");
    }
  }, []);

  const markNotificationRead = useCallback((id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  // ---- Scan lifecycle ----
  // No fake detection: "Run Scan Now" / the 60s timer simply refresh
  // alerts + dashboard from the real backend (where the actual SOC
  // scanner - virtual_soc/tasks.py - is what creates real SOCAlert
  // rows). Any alert not already seen this session is treated as a
  // genuine new detection and surfaced via toast/notification.

  const runScan = useCallback(() => {
    setScan((prev) => ({ ...prev, status: "Running" }));

    if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    scanTimeoutRef.current = setTimeout(async () => {
      const now = new Date().toISOString();
      const freshAlerts = await fetchAlerts();
      fetchDashboard();

      const newAlerts = freshAlerts.filter((a) => !knownAlertIdsRef.current.has(a.id));
      newAlerts.forEach((a) => knownAlertIdsRef.current.add(a.id));

      setScan((prev) => ({
        ...prev,
        status: "Completed",
        lastScanTime: now,
        nextScanTime: new Date(Date.now() + SCAN_INTERVAL_MS).toISOString(),
        issuesFound: prev.issuesFound + newAlerts.length,
        latestDetection: newAlerts.length
          ? { severity: newAlerts[0].severity, time: newAlerts[0].detectionTime || now }
          : prev.latestDetection,
      }));

      if (newAlerts.length === 0) {
        toast.success("Scan completed - no new issues found", { icon: "✅" });
        pushNotification({
          category: "Scan Completed",
          message: "Scan completed - no new issues detected.",
          link: { tab: "dashboard" },
        });
      } else {
        toast.success(`Scan completed - ${newAlerts.length} new issue(s) found`, { icon: "⚠️" });
        newAlerts.forEach((a) => {
          toast(`${a.severity.toUpperCase()}: ${a.name} detected on ${a.assetName}.`, {
            icon: "🚨",
            duration: 6000,
            style:
              a.severity === "critical"
                ? { background: "#7f1d1d", color: "#fff" }
                : a.severity === "high"
                ? { background: "#7c2d12", color: "#fff" }
                : undefined,
          });
          pushNotification({
            category:
              a.severity === "critical" ? "Critical Alert" :
              a.severity === "high" ? "High Alert" :
              a.severity === "medium" ? "Medium Alert" : "Low Alert",
            message: `${a.severity.toUpperCase()}: ${a.name} detected on ${a.assetName} at ${fmtTime(a.detectionTime)}.`,
            link: { tab: "alerts", id: a.id },
          });
        });
      }
    }, SCAN_DURATION_MS);
  }, [fetchAlerts, fetchDashboard, pushNotification]);

  const runScanNow = useCallback(() => {
    runScan();
  }, [runScan]);

  // ---- Initial load: fetch everything Dashboard needs once, on
  // SOC page mount. Reports is fetched lazily (see fetchReports'
  // reportsLoadedRef guard) from its own tab instead, since Dashboard
  // doesn't need it - demonstrating the "fetch on tab open, no
  // duplicate calls" pattern where it naturally applies. ----

  useEffect(() => {
    if (startedRef.current) return; // guard against double-invoke in StrictMode
    startedRef.current = true;

    fetchDashboard();
    fetchAlerts();
    fetchIncidents();
    fetchInvestigations();
    fetchEvents();
    fetchAssets();
    fetchResponses();
    fetchThreats();

    scanIntervalRef.current = setInterval(runScan, SCAN_INTERVAL_MS);

    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
      startedRef.current = false;
    };
    // eslint-disable-next-line
  }, []);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const value = {
    // domain data
    assets,
    events,
    alerts,
    incidents,
    investigations,
    responses,
    threats,
    reports,
    dashboardStats,
    notifications,
    unreadCount,
    scan,

    // loading / error state per domain
    loading,
    errors,

    // navigation
    setNavigator,
    navigateTo,

    // scan controls
    runScanNow,

    // fetchers (exposed for lazy/tab-triggered loads + manual retry)
    fetchDashboard,
    fetchAlerts,
    fetchIncidents,
    fetchInvestigations,
    fetchEvents,
    fetchAssets,
    fetchResponses,
    fetchThreats,
    fetchReports,

    // mutations
    generateReport,
    escalateToIncident,
    openInvestigation,
    triggerResponseAction,
    updateResponseActionStatus,
    updateAlertStatus,
    markAlertRead,
    updateIncidentStatus,
    updateInvestigationStatus,
    markNotificationRead,
    markAllNotificationsRead,
  };

  return <SOCContext.Provider value={value}>{children}</SOCContext.Provider>;
};
